import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts } from './useProducts';
import { useSiteSettings } from './SiteSettingsContext';
import { findProductInCatalog } from './catalog/findProduct';
import { productGroupKey, shopPathForProduct } from './productPaths';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import AddToCartButton from './cart/AddToCartButton';
import { productCharPersonalizationEnabled } from './productPersonalization';
import ProductPrice from './catalog/ProductPrice';
import { productEffectivePrice } from './productPricing';
import {
  hrefForSameSiteAsset,
  productGalleryImagePaths,
  productHasVideo,
  primaryVideoSource,
  videoPosterSrc,
} from './productMediaUtils';
import VideoModal from './VideoModal';
import ImageModal from './ImageModal';
import { isOutOfStock, normalizeStockQty } from './cart/cartUtils';
import WishlistHeartButton from './wishlist/WishlistHeartButton';
import ProductReviews from './product/ProductReviews';
import ProductRefId from './catalog/ProductRefId';

export default function ProductDetailPage() {
  const { categorySlug, productId } = useParams();
  const navigate = useNavigate();
  const { products, categories, loading } = useProducts();
  const { settings: site } = useSiteSettings();
  const [imageIndex, setImageIndex] = useState(0);
  const [imageModal, setImageModal] = useState({ show: false, image: '', alt: '' });
  const [videoModal, setVideoModal] = useState({ show: false, video: '', alt: '' });
  const [alertEmail, setAlertEmail] = useState('');
  const [alertMsg, setAlertMsg] = useState('');

  const product = useMemo(
    () => findProductInCatalog(products, categorySlug, productId),
    [products, categorySlug, productId]
  );

  const catMeta = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const galleryPaths = useMemo(
    () => (product ? productGalleryImagePaths(product) : []),
    [product]
  );

  const related = useMemo(() => {
    if (!product) return [];
    const list = products[categorySlug] || [];
    const g = productGroupKey(product);
    return list
      .filter(
        (p) =>
          String(p.id) !== String(product.id) &&
          (String(p.type || '') === g || String(p.subcategory || '') === g)
      )
      .slice(0, 4);
  }, [product, products, categorySlug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setImageIndex(0);
  }, [categorySlug, productId]);

  useEffect(() => {
    if (!loading && !product) {
      navigate('/404', { replace: true });
    }
  }, [loading, product, navigate]);

  if (loading || !product) {
    return (
      <div className="store-page store-page--loading py-5 text-center">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  const title = formatProductTitle(product.name);
  const collectionPath = shopPathForProduct(product);
  const collectionLabel =
    catMeta?.name || formatDisplayTitle(String(categorySlug || '').replace(/-/g, ' '));
  const stockQty = normalizeStockQty(product.stock_qty);
  const outOfStock = isOutOfStock(stockQty);
  const price = productEffectivePrice(product);
  const hasVideo = productHasVideo(product);
  const videoSrc = primaryVideoSource(product);
  const mainImage = galleryPaths[imageIndex] || galleryPaths[0] || '';
  const hasMultipleImages = galleryPaths.length > 1;
  const store = site.store_copy || {};
  const pdpBullets = store.pdp_bullets || [];
  const whatsappTemplate = store.whatsapp_product_template || "Hi, I'm interested in {title} (ID: {id}) on Saloni Designs.";
  const whatsappText = whatsappTemplate
    .replace(/\{title\}/g, title)
    .replace(/\{id\}/g, String(product.id));
  const whatsappHref = `${site.whatsapp_href || 'https://wa.me/919680384332'}?text=${encodeURIComponent(whatsappText)}`;

  const goGallery = (direction) => {
    if (!hasMultipleImages) return;
    setImageIndex((prev) => {
      if (direction === 'next') return (prev + 1) % galleryPaths.length;
      return prev === 0 ? galleryPaths.length - 1 : prev - 1;
    });
  };

  const openImageLightbox = () => {
    if (!mainImage) return;
    setImageModal({ show: true, image: hrefForSameSiteAsset(mainImage), alt: title });
  };

  return (
    <div className="store-page product-detail-page">
      <Helmet>
        <title>{title} | {collectionLabel} | Saloni Designs</title>
        <meta
          name="description"
          content={String(product.description || `${title} — handcrafted ${collectionLabel} from Saloni Designs.`).slice(0, 160)}
        />
      </Helmet>

      <div className="container store-page__container py-4 py-lg-5">
        <nav className="pdp-breadcrumb small mb-3" aria-label="Breadcrumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden> / </span>
          <Link to={collectionPath}>{collectionLabel}</Link>
          <span aria-hidden> / </span>
          <span className="text-muted">{title}</span>
        </nav>

        <div className="row g-4 g-lg-5">
          <div className="col-lg-6">
            <div className="pdp-gallery">
              <div className="pdp-gallery__stage">
                {hasMultipleImages ? (
                  <div className="pdp-gallery__nav-layer" onClick={(e) => e.stopPropagation()} role="presentation">
                    <button
                      type="button"
                      className="pdp-gallery__nav-btn pdp-gallery__nav-btn--side pdp-gallery__nav-btn--prev"
                      onClick={() => goGallery('prev')}
                      aria-label="Previous image"
                    >
                      ❮
                    </button>
                    <button
                      type="button"
                      className="pdp-gallery__nav-btn pdp-gallery__nav-btn--side pdp-gallery__nav-btn--next"
                      onClick={() => goGallery('next')}
                      aria-label="Next image"
                    >
                      ❯
                    </button>
                    <span className="pdp-gallery__counter" aria-live="polite">
                      {imageIndex + 1} / {galleryPaths.length}
                    </span>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="pdp-gallery__main border-0 p-0 bg-transparent w-100"
                  onClick={() => {
                    if (hasVideo && videoSrc) {
                      setVideoModal({ show: true, video: videoSrc, alt: title, noSound: true });
                    } else {
                      openImageLightbox();
                    }
                  }}
                >
                {hasVideo && videoSrc ? (
                  <div className="pdp-gallery__video-wrap">
                    <img src={videoPosterSrc(product)} alt={title} className="pdp-gallery__img" />
                    <span className="pdp-gallery__play" aria-hidden>
                      ▶
                    </span>
                  </div>
                ) : (
                  <img
                    src={hrefForSameSiteAsset(mainImage)}
                    alt={title}
                    className="pdp-gallery__img"
                  />
                )}
                </button>
              </div>
              {hasMultipleImages ? (
                <div className="pdp-gallery__thumbs">
                  {galleryPaths.map((path, idx) => (
                    <button
                      key={path}
                      type="button"
                      className={`pdp-gallery__thumb${idx === imageIndex ? ' pdp-gallery__thumb--active' : ''}`}
                      onClick={() => setImageIndex(idx)}
                    >
                      <img src={hrefForSameSiteAsset(path)} alt="" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="col-lg-6">
            <h1 className="pdp-title store-page__title">{title}</h1>
            <ProductRefId id={product.id} className="product-id pdp-product-id" />
            {productCharPersonalizationEnabled(product) ? (
              <span className="badge rounded-pill mb-2 product-detail-monogram-badge">✦ Monogram — pick your letter</span>
            ) : product.type === 'personalized' ? (
              <span className="badge rounded-pill text-bg-secondary mb-2">Personalized</span>
            ) : null}
            <p className="pdp-price mb-0">
              <ProductPrice product={product} showBadge />
            </p>
            {outOfStock ? (
              <p className="text-danger small fw-semibold">Out of stock</p>
            ) : stockQty !== null ? (
              <p className="small text-muted">{stockQty} available</p>
            ) : null}

            {product.description ? (
              <div className="pdp-description">
                <h2 className="h6">{store.pdp_about_heading}</h2>
                <p className="text-muted mb-0">{product.description}</p>
              </div>
            ) : null}

            <ul className="pdp-bullets small text-muted">
              {pdpBullets.map((bullet, i) => (
                <li key={`pdp-bullet-${i}`}>
                  {bullet.text}
                  {bullet.link_label && bullet.link_href ? (
                    <>
                      {' '}
                      <Link to={bullet.link_href}>{bullet.link_label}</Link>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>

            <div className="pdp-actions d-flex flex-wrap gap-2 mt-4">
              {price > 0 && !outOfStock ? (
                <AddToCartButton product={product} categorySlug={categorySlug} className="flex-grow-1" />
              ) : null}
              <WishlistHeartButton
                product={product}
                categorySlug={categorySlug}
                size="md"
                showLabel
                className="btn btn-outline-secondary btn-lg rounded-pill wishlist-heart-btn--pdp"
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-lg rounded-pill"
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) void navigator.share({ title, url });
                  else {
                    void navigator.clipboard.writeText(url);
                    window.alert('Link copied.');
                  }
                }}
              >
                Share
              </button>
              <a
                href={whatsappHref}
                className="btn btn-outline-secondary btn-lg rounded-pill"
                target="_blank"
                rel="noopener noreferrer"
              >
                Ask on WhatsApp
              </a>
            </div>
            {outOfStock ? (
              <form
                className="mt-3 p-3 border rounded"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setAlertMsg('');
                  try {
                    const r = await fetch('/api/customer.php', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        action: 'stock_alert',
                        email: alertEmail.trim(),
                        category_slug: categorySlug,
                        product_id: product.id,
                      }),
                    });
                    const d = await r.json();
                    setAlertMsg(d.message || d.error || (d.success ? 'Subscribed.' : 'Could not subscribe.'));
                  } catch {
                    setAlertMsg('Could not subscribe.');
                  }
                }}
              >
                <p className="small fw-semibold mb-2">Notify me when back in stock</p>
                <div className="input-group input-group-sm">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Your email"
                    value={alertEmail}
                    onChange={(e) => setAlertEmail(e.target.value)}
                    required
                  />
                  <button type="submit" className="btn btn-primary">
                    Notify
                  </button>
                </div>
                {alertMsg ? <p className="small mt-2 mb-0">{alertMsg}</p> : null}
              </form>
            ) : null}
            {price <= 0 ? (
              <p className="small text-muted mt-2 mb-0">
                This design may need a custom quote — message us on WhatsApp with the product name.
              </p>
            ) : null}
          </div>
        </div>

        <ProductReviews categorySlug={categorySlug} productId={product.id} />

        {related.length > 0 ? (
          <section className="pdp-related mt-5 pt-4 border-top">
            <h2 className="h5 mb-3">More in this collection</h2>
            <div className="row g-3">
              {related.map((p) => (
                <div key={p.id} className="col-6 col-md-3">
                  <Link
                    to={`/product/${encodeURIComponent(categorySlug)}/${encodeURIComponent(p.id)}`}
                    className="pdp-related-card text-decoration-none"
                  >
                    <img
                      src={hrefForSameSiteAsset(productGalleryImagePaths(p)[0] || '')}
                      alt={formatProductTitle(p.name)}
                      className="pdp-related-card__img"
                      loading="lazy"
                    />
                    <span className="pdp-related-card__name">{formatProductTitle(p.name)}</span>
                    <span className="pdp-related-card__price">
                      <ProductPrice product={p} showBadge={false} />
                    </span>
                  </Link>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <ImageModal
        show={imageModal.show}
        image={
          imageModal.show && galleryPaths[imageIndex]
            ? hrefForSameSiteAsset(galleryPaths[imageIndex])
            : imageModal.image
        }
        alt={imageModal.alt}
        onClose={() => setImageModal({ show: false, image: '', alt: '' })}
        onNext={() => goGallery('next')}
        onPrev={() => goGallery('prev')}
        hasMultipleImages={hasMultipleImages}
        currentIndex={imageIndex}
        totalImages={galleryPaths.length}
      />
      <VideoModal
        show={videoModal.show}
        video={videoModal.video}
        alt={videoModal.alt}
        noSound={videoModal.noSound}
        onClose={() => setVideoModal({ show: false, video: '', alt: '' })}
      />
    </div>
  );
}
